import React, { useState } from 'react';
import { Share2, Copy, Check, MessageCircle, Send, Globe } from 'lucide-react';

interface DashboardShareToolsProps {
  referralCode: string;
  referralUrl: string;
}

export const DashboardShareTools: React.FC<DashboardShareToolsProps> = ({
  referralCode,
  referralUrl,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(referralUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleWhatsApp = () => {
    const text = encodeURIComponent(
      `Check out Conversora — automate orders, customer service, and abandoned cart recovery with AI: ${referralUrl}`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const handleEmail = () => {
    const subject = encodeURIComponent('Scale your store with Conversora AI');
    const body = encodeURIComponent(
      `Hi,\n\nI recommend checking out Conversora for your e-commerce store:\n${referralUrl}\n\nIt handles automated customer chat, order updates, and marketing recovery.`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <div className="bg-[#0E1015] border border-zinc-800/80 rounded-2xl p-6">
      <div className="flex items-center justify-between pb-4 border-b border-zinc-800 mb-5">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-indigo-600/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
            <Share2 className="w-4 h-4" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-white">Share Your Referral Link</h2>
            <p className="text-xs text-zinc-400">
              Recommend Conversora to shop owners, agency clients, and online merchants
            </p>
          </div>
        </div>
        <span className="text-xs font-mono px-2 py-0.5 rounded bg-zinc-800 text-zinc-300">
          Code: {referralCode}
        </span>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-zinc-300 mb-1.5">
            Direct Tracking Link
          </label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={referralUrl}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-xl py-2 px-3 text-xs font-mono text-zinc-200 select-all focus:outline-none"
            />
            <button
              type="button"
              onClick={handleCopy}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-medium flex items-center gap-1.5 transition shrink-0 cursor-pointer shadow-xs"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              <span>{copied ? 'Copied' : 'Copy'}</span>
            </button>
          </div>
        </div>

        <div className="pt-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleWhatsApp}
            className="flex items-center gap-2 px-3 py-2 bg-emerald-950/40 hover:bg-emerald-900/50 text-emerald-300 border border-emerald-500/30 rounded-xl text-xs font-medium transition cursor-pointer"
          >
            <MessageCircle className="w-3.5 h-3.5 text-emerald-400" />
            <span>Share via WhatsApp</span>
          </button>

          <button
            type="button"
            onClick={handleEmail}
            className="flex items-center gap-2 px-3 py-2 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border border-zinc-800 rounded-xl text-xs font-medium transition cursor-pointer"
          >
            <Send className="w-3.5 h-3.5 text-zinc-400" />
            <span>Share via Email</span>
          </button>

          <a
            href="https://conversora.io"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 px-3 py-2 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border border-zinc-800 rounded-xl text-xs font-medium transition cursor-pointer"
          >
            <Globe className="w-3.5 h-3.5 text-zinc-400" />
            <span>Visit Platform</span>
          </a>
        </div>
      </div>
    </div>
  );
};
